import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { FaChevronRight } from "react-icons/fa";
import Avatar from "../retro/avatar/avatar.component";
import TinyContextMenu from "../tiny-context-menu/TinyContextMenu";
import { FiUsers, FiX } from "react-icons/fi";
import { db } from "../../firebase/firebase.utils";
import {
  cancelInvite,
  loadMemberProfiles,
} from "../../firebase/memberFunctions";
import permissions from "../../utils/permissions";
import PrMembers from "./prmembers.component";
import "./ProjectMembersButton.scss";
import "./members.styles.scss";

const { getPermissions, getOwnerUid, getInviteRoleKey, ROLE_LABELS } =
  permissions;

export default function ProjectMembersButton(props) {
  const uid = useSelector((state) => state.user.currentUser?.uid);
  return (
    <ProjectMembersControl
      key={`${uid}:${props.spaceId}:${props.stationId}`}
      {...props}
      uid={uid}
    />
  );
}

function ProjectMembersControl({
  spaceId,
  stationId,
  uid,
  hidden = false,
  toolbar = false,
}) {
  const [open, setOpen] = useState(false);
  const stationData = useSelector((state) => state.space.stationData);
  const spaces = useSelector((state) => state.space.spaceData);
  const cachedPeople = useSelector((state) => state.user.users);
  const [loadedPeople, setLoadedPeople] = useState({});
  const currentStation = (stationData || []).find(
    (item) =>
      (item.stationId || item.id) === stationId &&
      (item.spaceId || item.fromSpaceId) === spaceId,
  );
  const ids = [...new Set(currentStation?.members || [])];
  const space = (spaces || []).find(
    (item) => (item.spaceId || item.id) === spaceId,
  );
  const access = getPermissions(
    space && { ...space, spaceId },
    currentStation && { ...currentStation, fromSpaceId: spaceId },
    uid,
  );
  const workspaceIds =
    toolbar && access.canViewWorkspace && access.canViewProject
      ? [
          ...new Set(
            [getOwnerUid(space), ...(space?.members || [])].filter(Boolean),
          ),
        ].filter((id) => !ids.includes(id))
      : [];
  const visibleWorkspaceIds = workspaceIds.slice(0, 2);
  const people = { ...loadedPeople };
  (cachedPeople || []).forEach((person) => {
    if (person?.uid) people[person.uid] = person;
  });
  const visibleIds = ids.slice(0, 4);
  const missingKey = JSON.stringify(
    [...visibleIds, ...visibleWorkspaceIds].filter((id) => !(id in people)),
  );
  useEffect(() => {
    let active = true;
    const missing = JSON.parse(missingKey);
    if (!missing.length) return;
    loadMemberProfiles(missing)
      .then((profiles) => {
        if (active)
          setLoadedPeople((old) => ({
            ...old,
            ...Object.fromEntries(
              missing.map((id) => [id, profiles[id] || null]),
            ),
          }));
      })
      .catch(() => {
        if (active)
          setLoadedPeople((old) => ({
            ...old,
            ...Object.fromEntries(missing.map((id) => [id, null])),
          }));
      });
    return () => {
      active = false;
    };
  }, [missingKey]);
  const location = useLocation();
  const triggerRef = useRef(null);

  // Profile links and route changes should dismiss the project modal too.
  useEffect(() => {
    setOpen(false);
  }, [location.key]);
  if (!uid || !spaceId || !stationId) return null;

  function renderStack(allIds, small = false) {
    const limit = small ? 2 : 4;
    const visible = allIds.slice(0, limit);
    const size = small ? (28 * 2) / 3 : 28;
    return (
      <span
        className={`project-members-button__stack${small ? " project-members-button__stack--workspace" : ""}`}
        aria-hidden="true"
      >
        {visible.map((id, index) => (
          <span
            key={id}
            className="project-members-button__avatar"
            style={{ zIndex: visible.length - index }}
          >
            {people[id] ? (
              <Avatar
                user={people[id]}
                src={people[id].imageUrl}
                size={size}
                tiny
                backgroundColor={
                  toolbar ? "var(--project-member-avatar-bg)" : undefined
                }
              />
            ) : (
              <FiUsers size={small ? 12 : 16} />
            )}
          </span>
        ))}
        {allIds.length > visible.length && (
          <span className="project-members-button__more">
            +{allIds.length - visible.length}
          </span>
        )}
      </span>
    );
  }

  return (
    <>
      {!hidden && (
        <button
          ref={triggerRef}
          type="button"
          className={`project-members-button${toolbar ? " project-members-button--toolbar" : ""}${workspaceIds.length ? " project-members-button--with-workspace" : ""}`}
          style={
            workspaceIds.length
              ? {
                  "--project-workspace-color":
                    space.color || "var(--project-member-avatar-bg)",
                }
              : undefined
          }
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={`Project members and roles: ${ids.length} assigned${workspaceIds.length ? `, ${workspaceIds.length} other workspace members with access` : ""}`}
          title="Project members and roles"
        >
          <span className="project-members-button__rows">
            {ids.length ? (
              renderStack(ids)
            ) : (
              <span className="project-members-button__empty">
                <FiUsers size={20} />
                <span className="project-members-button__label">Members</span>
              </span>
            )}
            {workspaceIds.length > 0 && (
              <span
                className="project-members-button__workspace"
                title={`${workspaceIds.length} other workspace members with access`}
              >
                {renderStack(workspaceIds, true)}
              </span>
            )}
          </span>
        </button>
      )}
      {open && (
        <ProjectMembersModal
          spaceId={spaceId}
          stationId={stationId}
          uid={uid}
          triggerRef={triggerRef}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function ProjectMembersModal({ spaceId, stationId, uid, triggerRef, onClose }) {
  const [space, setSpace] = useState(null);
  const [station, setStation] = useState(null);
  const [spaceReady, setSpaceReady] = useState(false);
  const [stationReady, setStationReady] = useState(false);
  const [people, setPeople] = useState({});
  const [pending, setPending] = useState([]);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const busyRef = useRef(false);
  const dialogRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  // Keep the path available even for a project-only user without workspace data.
  const projectSpace = space || { spaceId };
  const access = getPermissions(projectSpace, station, uid);
  const loading = !spaceReady || !stationReady;

  useEffect(() => {
    const ref = db.collection("space").doc(spaceId);
    const stopSpace = ref.onSnapshot(
      (snapshot) => {
        setSpace(snapshot.exists ? { ...snapshot.data(), spaceId } : null);
        setSpaceReady(true);
      },
      () => {
        setSpace(null);
        setSpaceReady(true); // A direct project invitation doesn't require workspace UI access.
      },
    );
    const stopStation = ref
      .collection("stations")
      .doc(stationId)
      .onSnapshot(
        (snapshot) => {
          setStation(
            snapshot.exists
              ? { ...snapshot.data(), stationId, fromSpaceId: spaceId }
              : null,
          );
          setStationReady(true);
        },
        (err) => {
          setStation(null);
          setStationReady(true);
          setError(err.message || "Couldn't load project members.");
        },
      );
    return () => {
      stopSpace();
      stopStation();
    };
  }, [spaceId, stationId]);

  useEffect(() => {
    if (!access.canViewProject) {
      setPending([]);
      return;
    }
    return db
      .collection("invites")
      .where("inviterUid", "==", uid)
      .where("status", "==", "pending")
      .where("spaceId", "==", spaceId)
      .onSnapshot(
        (snapshot) => {
          setPending(
            snapshot.docs
              .map((document) => ({ ...document.data(), id: document.id }))
              .filter(
                (invite) =>
                  invite.targetType === "station" &&
                  invite.stationId === stationId,
              ),
          );
        },
        (err) => setError(err.message),
      );
  }, [uid, spaceId, stationId, access.canViewProject]);

  const workspaceIds =
    access.canViewProject && access.canViewWorkspace
      ? [
          ...new Set(
            [getOwnerUid(space), ...(space?.members || [])].filter(Boolean),
          ),
        ]
      : [];
  const profileKey = JSON.stringify(
    access.canViewProject
      ? [
          ...new Set([
            ...workspaceIds,
            ...(station?.members || []),
            ...pending.map((invite) => invite.inviteeUid),
          ]),
        ].sort()
      : [],
  );
  useEffect(() => {
    let active = true;
    loadMemberProfiles(JSON.parse(profileKey))
      .then((profiles) => {
        if (active) setPeople(profiles);
      })
      .catch((err) => {
        if (active) setError(err.message);
      });
    return () => {
      active = false;
    };
  }, [profileKey]);

  // Existing pickers/confirmation dialogs/menu portals stay above this modal.
  function nestedOverlayOpen() {
    return Boolean(
      document.querySelector(
        ".members-picker-layer, .boxLayer, .tiny-context-menu__menu",
      ),
    );
  }
  function requestClose() {
    if (
      busyRef.current ||
      dialogRef.current?.querySelector('[aria-busy="true"]') ||
      nestedOverlayOpen()
    )
      return;
    closeRef.current();
  }
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    const trigger = triggerRef.current;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    const onKeyDown = (event) => {
      if (nestedOverlayOpen()) return;
      if (event.key === "Escape") {
        event.preventDefault();
        requestCloseRef.current();
      }
      if (event.key === "Tab") {
        const buttons = [
          ...(dialogRef.current?.querySelectorAll(
            'button:not(:disabled), a[href], summary, input:not(:disabled), [tabindex="0"]',
          ) || []),
        ].filter(
          (element) =>
            !element.closest("[inert]") && element.getClientRects().length,
        );
        const first = buttons[0];
        const last = buttons[buttons.length - 1];
        if (!first) {
          event.preventDefault();
          return;
        }
        if (
          event.shiftKey &&
          (document.activeElement === first ||
            document.activeElement === dialogRef.current)
        ) {
          event.preventDefault();
          last.focus();
        } else if (
          !event.shiftKey &&
          (document.activeElement === last ||
            document.activeElement === dialogRef.current)
        ) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      if (trigger?.isConnected) trigger.focus();
    };
  }, [triggerRef]);

  async function cancel(id) {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusyId(id);
    setError("");
    try {
      await cancelInvite(id);
    } catch (err) {
      setError(err.message || "Couldn't cancel invitation.");
    } finally {
      busyRef.current = false;
      setBusyId(null);
    }
  }

  return createPortal(
    <div
      className="project-members-layer"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) requestClose();
      }}
    >
      <section
        className="project-members-dialog"
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Project members and roles"
      >
        <header className="project-members-dialog__header">
          <h2>Project members</h2>
          <button
            type="button"
            onClick={requestClose}
            aria-label="Close project members"
          >
            <FiX size={22} />
          </button>
        </header>
        <div className="project-members-dialog__content">
          {!!error && (
            <p role="alert" className="members__error">
              {error}
            </p>
          )}
          {loading ? (
            <p role="status">Loading members…</p>
          ) : !access.canViewProject ? (
            <p>This project is unavailable or you no longer have access.</p>
          ) : (
            <>
              <div className="project_members">
                <PrMembers
                  space={projectSpace}
                  station={station}
                  people={people}
                  pending={pending}
                  memberData={workspaceIds.map(
                    (id) => people[id] || { uid: id },
                  )}
                />
              </div>
              {pending.length > 0 && (
                <details className="members__pending">
                  <summary>
                    Your pending invitations <span>({pending.length})</span>
                    <FaChevronRight size={12} aria-hidden="true" />
                  </summary>
                  {pending.map((invite) => (
                    <div
                      key={invite.id}
                      className="members__pending-row"
                      aria-busy={busyId === invite.id}
                    >
                      <div>
                        <strong>
                          {people[invite.inviteeUid]?.userName ||
                            invite.inviteeName ||
                            invite.inviteeUid}
                        </strong>
                        <p>{ROLE_LABELS[getInviteRoleKey(invite)]}</p>
                      </div>
                      <TinyContextMenu
                        items={[
                          {
                            key: "cancel",
                            label:
                              busyId === invite.id
                                ? "Cancelling…"
                                : "Cancel invitation",
                            icon: <FiX />,
                            disabled: Boolean(busyId),
                            onPress: () => cancel(invite.id),
                          },
                        ]}
                      />
                    </div>
                  ))}
                </details>
              )}
            </>
          )}
        </div>
      </section>
    </div>,
    document.body,
  );
}
